import React from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import en from "../../public/locale/en.json";
import { LANGUAGES } from "./index";

interface LocaleStore {
  locale: Record<string, string>;
  currentLanguage: string;
  isHydrated: boolean;
  setLocale: (locale: Record<string, string>) => void;
  setCurrentLanguage: (language: string) => void;
  setHydrated: (hydrated: boolean) => void;
}

export const useLocaleStore = create<LocaleStore>()(
  persist(
    (set) => ({
      locale: en, // Initialize with English translations
      currentLanguage: "en", // Track current language separately
      isHydrated: false,
      setLocale: (locale) => set({ locale }),
      setCurrentLanguage: (language) => {
        set({ currentLanguage: language });
        const translations = LANGUAGES[language] || LANGUAGES.en;
        if (translations) {
          set({ locale: translations });
        }
      },
      setHydrated: (hydrated) => set({ isHydrated: hydrated }),
    }),
    {
      name: "fluid-language-storage",
      partialize: (state) => ({ currentLanguage: state.currentLanguage }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Ensure both currentLanguage and locale are synchronized
          const translations = LANGUAGES[state.currentLanguage] || LANGUAGES.en;
          if (translations) {
            state.locale = translations;
          }
          // Mark as hydrated after rehydration
          state.setHydrated(true);
        }
      },
    }
  )
);

export const useLocale = () => {
  const locale = useLocaleStore((state) => state.locale);
  const currentLanguage = useLocaleStore((state) => state.currentLanguage);
  const isHydrated = useLocaleStore((state) => state.isHydrated);
  const setCurrentLanguage = useLocaleStore(
    (state) => state.setCurrentLanguage
  );
  const setHydrated = useLocaleStore((state) => state.setHydrated);

  // Mark as hydrated on client side
  React.useEffect(() => {
    setHydrated(true);
  }, [setHydrated]);

  // Ensure locale is synchronized with currentLanguage after hydration
  React.useEffect(() => {
    if (isHydrated) {
      const translations = LANGUAGES[currentLanguage];
      if (translations && locale !== translations) {
        setCurrentLanguage(currentLanguage);
      }
    }
  }, [currentLanguage, locale, setCurrentLanguage, isHydrated]);

  return (key: string, fallback?: string) => {
    return locale[key] || fallback || key;
  };
};
